import { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import useExchangeRate from '../hooks/useExchangeRate';

const API = 'https://affinityconcept.tech/api';

const SOURCES = ['Salary', 'Rental Income', 'Dividends', 'Commission', 'Freelance', 'Business', 'Bonus', 'Interest', 'Stock Sale', 'Crypto Sale', 'Other'];

const FORM_DEFAULT = {
  source: 'Salary',
  amount: '',
  currency: 'AED',
  is_recurring: true,
  recurring_frequency: 'monthly',
  date: new Date().toISOString().split('T')[0],
  notes: ''
};

export default function Income({ token, onLogout }) {
  const [income, setIncome] = useState([]);
  const [user, setUser] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(FORM_DEFAULT);
  const { toAED } = useExchangeRate();

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/income`, { headers }).then(r => setIncome(r.data));
    axios.get(`${API}/me`, { headers }).then(r => setUser(r.data));
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      amount: parseFloat(form.amount),
      date: form.is_recurring ? undefined : form.date,
    };
    if (editItem) {
      await axios.put(`${API}/income/${editItem.id}`, payload, { headers });
    } else {
      await axios.post(`${API}/income`, payload, { headers });
    }
    const res = await axios.get(`${API}/income`, { headers });
    setIncome(res.data);
    setShowForm(false);
    setEditItem(null);
    setForm(FORM_DEFAULT);
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setForm({
      source: item.source,
      amount: item.amount,
      currency: item.currency,
      is_recurring: item.is_recurring,
      recurring_frequency: item.recurring_frequency || 'monthly',
      date: item.date ? item.date.split('T')[0] : new Date().toISOString().split('T')[0],
      notes: item.notes || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this income entry?')) return;
    await axios.delete(`${API}/income/${id}`, { headers });
    setIncome(income.filter(i => i.id !== id));
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditItem(null);
    setForm(FORM_DEFAULT);
  };

const now = new Date();

  // Monthly recurring
  const monthly = income
    .filter(i => i.is_recurring && i.recurring_frequency === 'monthly')
    .reduce((sum, i) => sum + toAED(i.amount, i.currency), 0);

  // This month total (recurring + one-time this month)
  const thisMonthOneTime = income
    .filter(i => {
      if (i.is_recurring) return false;
      const d = new Date(i.date);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    })
    .reduce((sum, i) => sum + toAED(i.amount, i.currency), 0);
  const thisMonth = monthly + thisMonthOneTime;

  // YTD (Year to Date)
  const ytd = income
    .filter(i => {
      if (i.is_recurring) {
        return true; // count recurring as monthly × months elapsed
      }
      const d = new Date(i.date);
      return d.getFullYear() === now.getFullYear();
    })
    .reduce((sum, i) => {
      if (i.is_recurring) {
        const monthsElapsed = now.getMonth() + 1;
        return sum + toAED(i.amount, i.currency) * monthsElapsed;
      }
      return sum + toAED(i.amount, i.currency);
    }, 0);

  const oneTime = income
    .filter(i => !i.is_recurring)
    .reduce((sum, i) => sum + toAED(i.amount, i.currency), 0);

  const recurring = income.filter(i => i.is_recurring);
  const nonRecurring = income
    .filter(i => !i.is_recurring)
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <Sidebar onLogout={onLogout} user={user} />
      <main className="flex-1 overflow-y-auto p-4 md:p-8 pt-16 md:pt-8">

        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Income</h1>
            <p className="text-gray-500 text-sm">
              Monthly recurring: <span className="font-semibold text-green-500">AED {Math.round(monthly).toLocaleString()}</span>
              <span className="mx-2">•</span>
              One-time: <span className="font-semibold text-green-500">AED {Math.round(oneTime).toLocaleString()}</span>
            </p>
          </div>
          <button
            onClick={() => { setShowForm(!showForm); setEditItem(null); setForm(FORM_DEFAULT); }}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-xl text-sm font-medium transition">
            + Add
          </button>
        </div>

        {/* Summary cards */}
         <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <p className="text-xs text-gray-500 mb-1">This Month</p>
            <p className="text-2xl font-bold text-green-500">AED {Math.round(thisMonth).toLocaleString()}</p>
            <p className="text-xs text-gray-400 mt-1">
              Recurring + {thisMonthOneTime > 0 ? `AED ${Math.round(thisMonthOneTime).toLocaleString()} one-time` : 'no one-time'}
            </p>
          </div>
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <p className="text-xs text-gray-500 mb-1">Monthly Recurring</p>
            <p className="text-2xl font-bold text-green-500">AED {Math.round(monthly).toLocaleString()}</p>
            <p className="text-xs text-gray-400 mt-1">{recurring.length} recurring sources</p>
          </div>
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <p className="text-xs text-gray-500 mb-1">Year to Date ({now.getFullYear()})</p>
            <p className="text-2xl font-bold text-blue-500">AED {Math.round(ytd).toLocaleString()}</p>
            <p className="text-xs text-gray-400 mt-1">Recurring × {now.getMonth() + 1} months + commissions</p>
          </div>
        </div>
        {/* Add/Edit form */}
        {showForm && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-6">
            <h2 className="text-base font-semibold mb-4">{editItem ? 'Edit Income' : 'New Income'}</h2>
            <form onSubmit={handleAdd} className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <select value={form.source} onChange={e => setForm({...form, source: e.target.value})}
                className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                {SOURCES.map(s => <option key={s}>{s}</option>)}
              </select>
              <div className="flex gap-2">
                <input required type="number" step="any" placeholder="Amount"
                  value={form.amount} onChange={e => setForm({...form, amount: e.target.value})}
                  className="border border-gray-200 rounded-xl px-3 py-2 text-sm flex-1 focus:outline-none focus:ring-2 focus:ring-green-500" />
                <select value={form.currency} onChange={e => setForm({...form, currency: e.target.value})}
                  className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none">
                  <option>AED</option>
                  <option>USD</option>
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-600 px-1 md:col-span-2">
                <input type="checkbox" checked={form.is_recurring}
                  onChange={e => setForm({...form, is_recurring: e.target.checked})}
                  className="rounded" />
                Recurring income
              </label>
              {form.is_recurring ? (
                <select value={form.recurring_frequency}
                  onChange={e => setForm({...form, recurring_frequency: e.target.value})}
                  className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                  <option value="monthly">Monthly</option>
                  <option value="weekly">Weekly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                </select>
              ) : (
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Date received</label>
                  <input type="date" value={form.date}
                    onChange={e => setForm({...form, date: e.target.value})}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
              )}
              <input
                placeholder="Notes (optional) — e.g. Commission from Skyhigh Unit 710"
                value={form.notes}
                onChange={e => setForm({...form, notes: e.target.value})}
                className="border border-gray-200 rounded-xl px-3 py-2 text-sm md:col-span-2 focus:outline-none focus:ring-2 focus:ring-green-500" />
              <div className="md:col-span-2 flex gap-3">
                <button type="submit" className="bg-green-600 text-white px-5 py-2 rounded-xl text-sm font-medium">
                  {editItem ? 'Update' : 'Save'}
                </button>
                <button type="button" onClick={handleCancel}
                  className="border border-gray-200 px-5 py-2 rounded-xl text-sm">Cancel</button>
              </div>
            </form>
          </div>
        )}

        {/* Recurring income */}
        {recurring.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-4">
            <div className="px-4 py-3 border-b border-gray-50 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-700">🔄 Recurring Income</h3>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase">Source</th>
                  <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Frequency</th>
                  <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {recurring.map(i => (
                  <tr key={i.id} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="p-4">
                      <p className="text-sm font-medium text-gray-900">{i.source}</p>
                      {i.notes && <p className="text-xs text-gray-400 mt-0.5">{i.notes}</p>}
                      <p className="text-xs text-gray-400 md:hidden capitalize mt-0.5">{i.recurring_frequency}</p>
                    </td>
                    <td className="p-4 hidden md:table-cell">
                      <span className="bg-green-50 text-green-700 text-xs px-2 py-1 rounded-full capitalize">{i.recurring_frequency}</span>
                    </td>
                    <td className="p-4 text-right">
                      <p className="text-sm font-semibold text-green-500">{i.currency} {i.amount?.toLocaleString()}</p>
                      {i.currency !== 'AED' && (
                        <p className="text-xs text-gray-400">≈ AED {Math.round(toAED(i.amount, i.currency)).toLocaleString()}</p>
                      )}
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button onClick={() => handleEdit(i)} className="text-gray-400 hover:text-blue-500 text-xs transition">✏️</button>
                        <button onClick={() => handleDelete(i.id)} className="text-gray-300 hover:text-red-500 text-xs transition">✕</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* One-time income */}
        {nonRecurring.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-50 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-700">💰 One-time / Commissions</h3>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase">Source</th>
                  <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Date</th>
                  <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {nonRecurring.map(i => (
                  <tr key={i.id} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="p-4">
                      <p className="text-sm font-medium text-gray-900">{i.source}</p>
                      {i.notes && <p className="text-xs text-gray-400 mt-0.5">{i.notes}</p>}
                      <p className="text-xs text-gray-400 md:hidden mt-0.5">
                        {i.date ? new Date(i.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                      </p>
                    </td>
                    <td className="p-4 text-sm text-gray-500 hidden md:table-cell">
                      {i.date ? new Date(i.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                    </td>
                    <td className="p-4 text-right">
                      <p className="text-sm font-semibold text-blue-500">{i.currency} {i.amount?.toLocaleString()}</p>
                      {i.currency !== 'AED' && (
                        <p className="text-xs text-gray-400">≈ AED {Math.round(toAED(i.amount, i.currency)).toLocaleString()}</p>
                      )}
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button onClick={() => handleEdit(i)} className="text-gray-400 hover:text-blue-500 text-xs transition">✏️</button>
                        <button onClick={() => handleDelete(i.id)} className="text-gray-300 hover:text-red-500 text-xs transition">✕</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {income.length === 0 && !showForm && (
          <div className="bg-white rounded-2xl p-12 text-center text-gray-400 text-sm border border-gray-100">
            No income added yet. Click + Add to get started!
          </div>
        )}
      </main>
    </div>
  );
}
